import React, { useState, useEffect } from 'react';
import { apiUrl } from '../utils/api';
import PropTypes from 'prop-types';
import OtterLogo from './ui/OtterLogo';
import AuraLightBackground from './ui/AuraLightBackground';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, CheckCircle, QrCode } from 'lucide-react';
import LoadingSpinner from './ui/LoadingSpinner';
import Button from './ui/Button';
import FormField from './ui/FormField.jsx';
import { Input } from './ui/Input.jsx';
import { Capacitor } from '@capacitor/core';
import PasswordRequirements from './PasswordRequirements';
import { isPasswordValid, PASSWORD_RULE_MESSAGE } from '../utils/passwordPolicy';
import { safeReturnPath } from '../utils/returnPath';

const AuthPage = ({ mode: initialMode = 'login' }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Rücksprung nach dem Login (Okt 2026): ?next= oder state.from der geschützten
  // Route, nur interne Pfade aus der Whitelist in utils/returnPath.js.
  const returnTo = safeReturnPath(searchParams.get('next')) || safeReturnPath(location.state?.from);
  const [mode, setMode] = useState(initialMode);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  // Datenschutz-Haken (Art. 7 Abs. 1 DSGVO: Nachweis der Einwilligung, Zeitstempel im Backend)
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  // 🎫 Invitation-Code aus URL-Parameter
  const invitationCode = searchParams.get('invitation');
  const [invitationData, setInvitationData] = useState(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState('');
  // Manuelle Code-Eingabe (Plakat ohne Scanner): Kurzcode "ABCD-EFGH" oder alter UUID-Code
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  // 🎫 Invitation validieren wenn Code vorhanden
  useEffect(() => {
    const validateInvitation = async () => {
      if (!invitationCode) return;

      setInvitationLoading(true);
      setInvitationError('');

      try {
        const response = await fetch(apiUrl(`/api/v1/invitations/validate/${invitationCode}`));
        const data = await response.json();

        if (response.ok && data.success) {
          setInvitationData(data.data.invitation);
          // Bei Invitation automatisch auf Register-Mode wechseln
          setMode('register');
        } else {
          setInvitationError(data.error?.message || 'Ungültiger Einladungscode');
        }
      } catch (err) {
        console.error('Invitation validation error:', err);
        setInvitationError('Fehler bei der Validierung des Einladungscodes');
      } finally {
        setInvitationLoading(false);
      }
    };

    validateInvitation();
  }, [invitationCode]);

  const validateForm = () => {
    if (mode === 'register') {
      if (formData.password !== formData.confirmPassword) {
        setError('Die Passwörter stimmen nicht überein');
        return false;
      }
      // Passwort-Regel "Länge-zuerst" (zentral in utils/passwordPolicy)
      if (!isPasswordValid(formData.password)) {
        setError(PASSWORD_RULE_MESSAGE);
        return false;
      }
      if (!formData.name) {
        setError('Bitte gib deinen Namen ein');
        return false;
      }
      if (formData.name.trim().length < 2) {
        setError('Der Name muss mindestens 2 Zeichen lang sein');
        return false;
      }
      // Echte Namen erlaubt: Buchstaben (inkl. Umlaute/Akzente), Leerzeichen, Bindestrich, Apostroph, Punkt
      if (!/^[\p{L}][\p{L} .'-]*$/u.test(formData.name.trim())) {
        setError('Bitte gib einen gültigen Namen ein (nur Buchstaben, Leerzeichen, Bindestrich und Apostroph)');
        return false;
      }
      if (!privacyAccepted) {
        setError('Bitte bestätige, dass du die Datenschutzerklärung gelesen hast');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      if (mode === 'login') {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          if (returnTo) navigate(returnTo, { replace: true });
          else navigate('/app');
        } else {
          setError(result.error || 'Login fehlgeschlagen');
        }
      } else {
        // 🎫 Bei Invitation: invitationCode mit übergeben
        const result = await register(
          formData.email,
          formData.password,
          formData.name,
          invitationCode, // Optional - null wenn keine Invitation
          privacyAccepted
        );

        if (result.success) {
          if (result.requiresLogin) {
            // Registrierung hat geklappt, aber der Auto-Login nicht (kein Token).
            // Ohne Token wuerde das Profil-Setup sofort scheitern -> lieber sauber anmelden.
            setMode('login');
            setFormData({ name: '', email: formData.email, password: '', confirmPassword: '' });
            setInfo(result.message || 'Registrierung erfolgreich! Bitte melde dich jetzt an.');
            navigate('/login');
          } else if (result.requiresProfileSetup) {
            navigate('/profile-setup');
          } else {
            navigate('/app');
          }
        } else {
          setError(result.error || 'Registrierung fehlgeschlagen');
        }
      }
    } catch (error) {
      console.error(mode === 'login' ? 'Login error:' : 'Registration error:', error);
      setError(error.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const isNative = Capacitor.isNativePlatform();

  return (
    <div
      className="min-h-screen relative flex flex-col justify-center items-center p-4 font-['Poppins']"
      style={isNative ? {
        paddingTop: 'max(env(safe-area-inset-top, 16px), 16px)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 16px), 16px)'
      } : {}}
    >
      <AuraLightBackground intensity="normal" showAnimations={true} />
      <div className={`w-full max-w-md bg-surface/95 backdrop-blur-sm rounded-3xl shadow-token-xl relative z-10 ${isNative ? 'p-5' : 'p-8'}`}>
        <div className={`flex flex-col items-center ${isNative ? 'mb-4' : 'mb-8'}`}>
          <div className={`flex items-center justify-center gap-4 ${isNative ? 'mb-3' : 'mb-6'}`}>
            <OtterLogo />
          </div>
          <div className="w-full rounded-xl overflow-hidden bg-foreground/5 mb-4 shadow-token-md">
            <img
              src="/images/handshake.webp"
              alt="Otter Handshake"
              className="w-full h-auto object-contain rounded-xl"
              style={{ maxHeight: isNative ? '160px' : '200px' }}
            />
          </div>
        </div>

        {/* 🎫 Invitation-Status anzeigen */}
        {invitationLoading && (
          <div className="mb-4 p-4 bg-primary-soft border border-primary/30 rounded-xl">
            <div className="flex items-center gap-2 text-primary">
              <LoadingSpinner size="small" />
              <span className="text-sm">Einladung wird geprüft...</span>
            </div>
          </div>
        )}

        {invitationError && (
          <div role="alert" className="mb-4 p-4 bg-danger-soft border border-danger/30 rounded-xl">
            <div className="text-foreground text-sm">{invitationError}</div>
          </div>
        )}

        {invitationData && (
          <div className="mb-4 p-4 bg-success-soft border border-success/30 rounded-xl">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground font-medium text-sm">
                  Einladung von {invitationData.school?.name || 'deiner Schule'}
                </p>
                <div className="flex items-center gap-2 mt-1 text-muted-foreground text-xs">
                  <Building2 className="h-3 w-3" />
                  <span>Du wirst als {invitationData.role === 'TEACHER' ? 'Lehrkraft' : 'Schüler:in'} registriert</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div role="alert" className="mb-4 p-3 bg-danger-soft border border-danger/40 text-foreground rounded-xl text-sm">
            {error}
          </div>
        )}

        {info && (
          <div role="status" className="mb-4 p-3 bg-success-soft border border-success/40 text-foreground rounded-xl text-sm">
            {info}
          </div>
        )}

        {/* LOGIN-Formular */}
        {mode === 'login' && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="Email">
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                /* "username" statt des Vorgabewerts "email" aus ui/Input: Der
                   Schluesselbund und Google erkennen das Paar Benutzername +
                   Passwort daran als Anmeldung. Die E-Mail-Tastatur bleibt
                   (type="email"). */
                autoComplete="username"
                enterKeyHint="next"
              />
            </FormField>

            <FormField label="Passwort">
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                /* Ohne diese Angabe bietet kein Passwortmanager — weder der
                   iCloud-Schluesselbund noch Google noch 1Password — das
                   gespeicherte Passwort an. `go` beschriftet die Return-Taste
                   auf dem Telefon mit "Los" statt "Return". */
                autoComplete="current-password"
                enterKeyHint="go"
              />
            </FormField>

            <div className="text-right">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-primary hover:opacity-80 font-medium transition-colors"
              >
                Passwort vergessen?
              </button>
            </div>

            <Button type="submit" size="lg" fullWidth disabled={loading}>
              {loading ? 'Bitte warten…' : 'Anmelden'}
            </Button>
          </form>
        )}

        {/* REGISTRIERUNG – nur mit gültiger Einladung (verhindert Sackgasse) */}
        {mode === 'register' && invitationData && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="Name">
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                autoComplete="name"
                autoCapitalize="words"
                enterKeyHint="next"
              />
            </FormField>

            <FormField label="Email">
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                /* Wie beim Login: unter diesem Namen speichert der
                   Passwortmanager das neue Passwort. */
                autoComplete="username"
                enterKeyHint="next"
              />
            </FormField>

            <FormField label="Passwort">
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                /* "new-password" statt "current-password": Damit schlagen
                   Passwortmanager ein starkes neues Passwort vor, statt das
                   alte einzusetzen. */
                autoComplete="new-password"
              />
            </FormField>

            <FormField label="Passwort bestätigen">
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                autoComplete="new-password"
                enterKeyHint="go"
              />
            </FormField>

            <PasswordRequirements
              password={formData.password}
              confirmPassword={formData.confirmPassword}
              showMatch
            />

            {/* Einwilligung: Pflicht-Haken, Link auf die Datenschutzerklaerung.
                Die Schule informiert zusaetzlich (Elterninformation), EachOtter
                speichert nur den Zeitstempel des Hakens. */}
            <label className="flex items-start gap-3 text-sm text-foreground/80 cursor-pointer">
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="h-5 w-5 mt-0.5 shrink-0 rounded border-border accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
              <span>
                Ich habe die{' '}
                <a
                  href="/datenschutz"
                  target={Capacitor.isNativePlatform() ? '_self' : '_blank'}
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-hover underline"
                >
                  Datenschutzerklärung
                </a>{' '}
                gelesen und bin damit einverstanden, dass meine Daten für EachOtter an meiner Schule verarbeitet werden.
              </span>
            </label>

            <Button type="submit" size="lg" fullWidth disabled={loading}>
              {loading ? 'Bitte warten…' : 'Registrieren'}
            </Button>
          </form>
        )}

        {/* REGISTRIERUNG ohne gültige Einladung → Code eingeben oder scannen statt Sackgasse */}
        {mode === 'register' && !invitationData && !invitationLoading && (
          <div className="space-y-4">
            <div className="p-4 bg-primary-soft border border-primary/30 rounded-xl text-sm text-foreground/80">
              Zum Registrieren brauchst du den <span className="font-medium">Einladungscode deiner Schule</span>.
              Du findest ihn auf dem Plakat oder Flyer deiner Schule – oder du bekommst ihn von deiner Lehrkraft.
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const code = manualCode.trim();
                if (!code) return;
                navigate(`/register?invitation=${encodeURIComponent(code)}`);
              }}
              className="space-y-3"
            >
              <FormField label="Einladungscode">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  placeholder="z. B. ABCD-EFGH"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="font-mono tracking-wider"
                />
              </FormField>
              <Button type="submit" size="lg" fullWidth disabled={!manualCode.trim()}>
                Weiter
              </Button>
            </form>
            <Button size="lg" fullWidth variant="secondary" onClick={() => navigate('/scan-invitation')}>
              <QrCode className="h-5 w-5" aria-hidden="true" />
              QR-Code scannen
            </Button>
          </div>
        )}

        {/* Untere Navigation */}
        {mode === 'login' ? (
          <div className="mt-4">
            <div className="relative flex items-center justify-center my-3">
              <div className="w-full border-t border-border"></div>
              <span className="px-3 text-xs text-muted-foreground/70 bg-surface/95 absolute">oder</span>
            </div>
            <button
              onClick={() => navigate('/scan-invitation')}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary-soft text-primary border border-primary/30 rounded-xl hover:bg-primary/20 transition-colors duration-200 font-medium text-sm"
            >
              <QrCode className="h-4 w-4" />
              Neu an deiner Schule? Mit Einladung registrieren
            </button>
          </div>
        ) : (
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-foreground/80 hover:text-foreground font-medium transition-colors duration-200"
            >
              Bereits registriert? Anmelden
            </button>
          </div>
        )}

        {/* Hinweis für Schulen */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate({ pathname: '/', hash: 'schule-registrieren' })}
            className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors"
          >
            Bist du eine Schule? Hier registrieren
          </button>
        </div>
      </div>
    </div>
  );
};

AuthPage.propTypes = {
  mode: PropTypes.string
};

export default AuthPage;